/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { MasterDataType } from '../../../constants/etmf'
import type { EtmfListViewValue } from '../../../constants/etmf'
import { AclLevel, FsiLifecycleState, SystemFolderType } from '../../../constants/fsi'
import type { AclLevelType } from '../../../constants/fsi'

export interface EtmfActionItem {
  value: string
  action: () => void
  disabled?: boolean
  divider?: boolean
}

interface FileDetails {
  id: number
  name: string
  MDFType?: string
  mccObjUuid?: string
  mccParentObjUuid?: string
  workflow?: boolean
  systemFolderType?: string
  acl?: AclLevelType
  isBeforeApproveStep?: boolean
  state?: string
  actionFlags?: {
    canObsolete?: boolean
    [key: string]: any
  }
  [key: string]: any
}

interface GetEtmfActionsParams {
  file: FileDetails
  listView: EtmfListViewValue
  checkmate: any
  access: any
  accessTable: any
  generalHelper: any
  onViewDetails: () => void
  onShowProperties: () => void
  onAccessRights: () => void
  onUpdate: () => void
  onMakeObsolete: () => void
  onEditInCheckmate: () => void
}

/**
 * Gets the actions menu items for ETMF list pages (Products, Studies, Environments, Countries, Sites)
 * Based on legacy MDFTableMenu.jsx logic
 */
export function getEtmfMasterDataActions({
  file,
  listView,
  checkmate,
  access,
  accessTable,
  generalHelper,
  onViewDetails,
  onShowProperties,
  onAccessRights,
  onUpdate,
  onMakeObsolete,
  onEditInCheckmate
}: GetEtmfActionsParams): EtmfActionItem[] {
  const menu: EtmfActionItem[] = []

  // For Studies (TRIAL), show View Details first
  if (listView === MasterDataType.TRIAL) {
    menu.push({
      value: 'BROWSER_ACTION_VIEW_DETAILS',
      action: () => {
        console.log('View Details clicked for:', file.id, file.name)
        onViewDetails()
      }
    })

    // If in Checkmate and has mccObjUuid, add Edit in Clinical Architect option
    if (checkmate.isCheckmated() && file.mccObjUuid) {
      menu.push({
        value: 'ETMF_ACTION_EDIT_CA_STUDIES',
        action: () => {
          console.log('Edit in Clinical Architect clicked for study:', file.id, file.mccObjUuid)
          onEditInCheckmate()
        }
      })
    }
  }

  // Show Properties / RCM Attributes - with permission check
  const hasRequiredAcl = file.acl === AclLevel.READ || file.acl === AclLevel.WRITE || file.acl === AclLevel.DELETE
  if (
    !file.workflow &&
    file.systemFolderType !== SystemFolderType.WORKFLOWS &&
    (access.isAllowed('fsiProperties') ||
      (access.isAllowed(['CONTENT_ADMIN', 'POWER_USER', 'USER', 'AUDITOR', accessTable.stagingManagers]) &&
        hasRequiredAcl))
  ) {
    menu.push({
      value: listView === MasterDataType.TRIAL ? 'BROWSER_ACTION_RCM_ATTRIBUTES' : 'BROWSER_ACTION_SHOW_FILE',
      action: () => {
        console.log('Show Properties clicked for:', file.id, file.name)
        onShowProperties()
      }
    })
  }

  // For non-studies, show View Details after Properties
  if (listView !== MasterDataType.TRIAL) {
    menu.push({
      value: 'BROWSER_ACTION_VIEW_DETAILS',
      action: () => {
        console.log('View Details clicked for:', file.id, file.name)
        onViewDetails()
      }
    })
  }

  // Access Rights - with permission check
  const hasDeleteAcl = file.acl === AclLevel.DELETE
  if (
    !file.workflow &&
    file.systemFolderType !== SystemFolderType.WORKFLOWS &&
    (access.isAllowed('fsiSettings') ||
      (access.isAllowed(['CONTENT_ADMIN', accessTable.stagingManagers]) && hasDeleteAcl)) &&
    !file.isBeforeApproveStep &&
    file.state !== generalHelper.capitalizeString(FsiLifecycleState.DRAFT)
  ) {
    menu.push({
      value: 'BROWSER_ACTION_ACLS',
      action: () => {
        console.log('Access Rights clicked for:', file.id, file.name)
        onAccessRights()
      }
    })
  }

  // For Sites in Checkmate, add Edit in Clinical Architect option
  if (checkmate.isCheckmated() && file.mccObjUuid && listView === MasterDataType.SITE) {
    menu.push({
      value: 'ETMF_ACTION_EDIT_CA_SITES',
      action: () => {
        console.log('Edit Site in Clinical Architect clicked for:', file.id, file.mccObjUuid)
        onEditInCheckmate()
      }
    })
  }

  // Update action (Update Sites, Update Countries, Update Products, etc.) - always shown
  const updateActionLabel = (() => {
    switch (listView) {
      case MasterDataType.SITE:
        return 'ETMF_ACTION_UPDATE_SITES'
      case MasterDataType.COUNTRY:
        return 'ETMF_ACTION_UPDATE_COUNTRIES'
      case MasterDataType.PRODUCT:
        return 'ETMF_ACTION_UPDATE_PRODUCTS'
      case MasterDataType.ENVIRONMENT:
        return 'ETMF_ACTION_UPDATE_ENVIRONMENTS'
      case MasterDataType.TRIAL:
        return 'ETMF_ACTION_UPDATE_STUDIES'
      default:
        return 'ETMF_ACTION_UPDATE'
    }
  })()

  menu.push({
    value: updateActionLabel,
    action: () => {
      console.log('Update clicked for:', file.id, file.name, 'Type:', listView)
      onUpdate()
    }
  })

  // Make Obsolete (if item can be obsoleted)
  if (file.actionFlags?.canObsolete) {
    menu.push({
      value: 'BROWSER_ACTION_MAKE_OBSOLETE',
      action: () => {
        console.log('Make Obsolete clicked for:', file.id, file.name)
        onMakeObsolete()
      }
    })
  }

  return menu
}

interface GetPackagesActionsParams {
  item: any
  onEdit: () => void
  onRemove: () => void
  onExport: () => void
  onClone: () => void
  onActivity?: () => void
  onApplyPkg?: () => void
}

/**
 * Gets the actions menu items for Packages page
 */
export function getPackagesActions({
  item,
  onEdit,
  onRemove,
  onExport,
  onClone,
  onActivity,
  onApplyPkg
}: GetPackagesActionsParams): EtmfActionItem[] {
  const menu: EtmfActionItem[] = []

  menu.push({
    value: 'EDIT',
    action: () => {
      onEdit()
    }
  })

  menu.push(
    {
      value: 'ACTION_REMOVE',
      action: () => {
        onRemove()
      }
    },
    {
      value: 'ACTION_EXPORT',
      action: () => {
        onExport()
      }
    },
    {
      value: 'ACTION_CLONE_PACKAGE',
      action: () => {
        onClone()
      }
    },
    {
      value: 'ACTIVITY_TITLE',
      action: () => {
        onActivity && onActivity()
      }
    },
    {
      value: 'ACTION_APPLY_PACKAGE',
      action: () => {
        onApplyPkg && onApplyPkg()
      }
    }
  )

  return menu
}
